/**
 * Job Description Parser Zustand store (ADR-021).
 *
 * Manages state for parsing, listing, and managing job descriptions.
 * Uses the same sliced-store + immer conventions as useSiteStore and
 * useTailorStore.
 *
 * State machine:
 *   idle ──parse──> parsing ──ok──> idle (with selectedJD populated)
 *                       └──err──> idle (with error)
 */
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { jobDescriptionApi } from '../../services/jobDescription.api';
import { logger } from '../../services/logger.client';
import type {
  JobDescription,
  ParseJobDescriptionInput,
} from '../../types/job-description.types';

const log = logger.child({ module: 'jobDescriptionStore' });

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

interface JobDescriptionState {
  jobDescriptions: JobDescription[];
  selectedJD: JobDescription | null;
  isLoading: boolean;
  isParsing: boolean;
  error: string | null;
  /** Set to true after the first fetchJobDescriptions completes. */
  hasFetched: boolean;
}

interface JobDescriptionActions {
  parseJobDescription: (input: ParseJobDescriptionInput) => Promise<JobDescription | null>;
  fetchJobDescriptions: () => Promise<void>;
  fetchJobDescription: (id: string) => Promise<void>;
  deleteJobDescription: (id: string) => Promise<void>;
  createSiteFromJD: (id: string, templateId?: string) => Promise<{ id: string } | null>;
  clearError: () => void;
  clearSelected: () => void;
}

export type JobDescriptionStore = JobDescriptionState & { actions: JobDescriptionActions };

/* ------------------------------------------------------------------ */
/*  Store                                                             */
/* ------------------------------------------------------------------ */

export const useJobDescriptionStore = create<JobDescriptionStore>()(
  immer((set) => ({
    /* ---------- initial state ---------- */
    jobDescriptions: [],
    selectedJD: null,
    isLoading: false,
    isParsing: false,
    error: null,
    hasFetched: false,

    /* ---------- actions ---------- */
    actions: {
      parseJobDescription: async (input) => {
        set((state) => {
          state.isParsing = true;
          state.error = null;
        });

        try {
          log.info('Parsing job description', { source_type: input.source_type });
          const result = await jobDescriptionApi.parse(input);

          if ('error' in result) {
            log.warn('Job description parse returned error', { error: result.error, id: result.id });
            set((state) => {
              state.isParsing = false;
              state.error = result.error;
            });
            // If a DB record was created before the failure, refresh the
            // list so the failed record appears and the user can delete it.
            if (result.id) {
              jobDescriptionApi.list().then((data) => {
                set((state) => {
                  state.jobDescriptions = data;
                });
              }).catch(() => { /* swallow — error state is already set */ });
            }
            return null;
          }

          set((state) => {
            state.isParsing = false;
            state.selectedJD = result;
            state.jobDescriptions.unshift(result);
          });

          log.info('Job description parsed', { id: result.id, status: result.status });
          return result;
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to parse job description';
          log.error('Job description parse threw', { error: message });

          set((state) => {
            state.isParsing = false;
            state.error = message;
          });
          return null;
        }
      },

      fetchJobDescriptions: async () => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          log.info('Fetching job descriptions');
          const data = await jobDescriptionApi.list();

          set((state) => {
            state.isLoading = false;
            state.hasFetched = true;
            state.jobDescriptions = data;
          });

          log.info('Job descriptions fetched', { count: data.length });
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to load job descriptions';
          log.error('Failed to fetch job descriptions', { error: message });

          set((state) => {
            state.isLoading = false;
            state.hasFetched = true;
            state.error = message;
          });
        }
      },

      fetchJobDescription: async (id) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          log.info('Fetching job description', { id });
          const data = await jobDescriptionApi.get(id);

          set((state) => {
            state.isLoading = false;
            state.selectedJD = data;
          });
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to load job description';
          log.error('Failed to fetch job description', { error: message });

          set((state) => {
            state.isLoading = false;
            state.error = message;
          });
        }
      },

      deleteJobDescription: async (id) => {
        set((state) => {
          state.error = null;
        });

        try {
          log.info('Deleting job description', { id });
          await jobDescriptionApi.delete(id);

          set((state) => {
            state.jobDescriptions = state.jobDescriptions.filter((jd) => jd.id !== id);
            if (state.selectedJD?.id === id) {
              state.selectedJD = null;
            }
          });

          log.info('Job description deleted', { id });
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to delete job description';
          log.error('Failed to delete job description', { error: message });

          set((state) => {
            state.error = message;
          });
        }
      },

      createSiteFromJD: async (id, templateId?) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          log.info('Creating site from job description', { id });
          const site = await jobDescriptionApi.createSite(
            id,
            templateId ? { template_id: templateId } : undefined,
          );

          set((state) => {
            state.isLoading = false;
          });

          log.info('Site created from job description', { siteId: site.id });
          return site;
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to create site';
          log.error('Failed to create site from JD', { error: message });

          set((state) => {
            state.isLoading = false;
            state.error = message;
          });
          return null;
        }
      },

      clearError: () => {
        set((state) => {
          state.error = null;
        });
      },

      clearSelected: () => {
        set((state) => {
          state.selectedJD = null;
        });
      },
    },
  })),
);

/* ------------------------------------------------------------------ */
/*  Selector hooks (ADR-021 wrapper-hooks pattern)                    */
/* ------------------------------------------------------------------ */

/** Stable reference to actions -- never causes a re-render. */
export function useJobDescriptionActions() {
  return useJobDescriptionStore((s) => s.actions);
}
